import { useEffect, useState } from 'react';
import { Loader2, ShieldAlert } from 'lucide-react';
import type { PermissionRequest } from '../api/types';
import { api } from '../api/client';
import './PermissionBlock.css';

interface Props {
  sessionName: string;
  data: PermissionRequest;
  /** Optional: poll callback to refresh the agent state after sending. */
  onSent?: () => void;
}

/**
 * Renders Claude Code's BLOCKING permission prompt ("Do you want to
 * proceed?") as a prominent approval gate — distinct from the neutral
 * MultiChoiceBlock because the agent is HALTED here, not offering a menu
 * (Cloud IACP 0d45d2ef: this was previously mis-rendered as USER INPUT,
 * so the bot looked "active" while it was actually waiting for the user).
 *
 * Send mechanism is identical to MultiChoiceBlock: clicking an option
 * sends its digit (1-9) as a single keypress to the tmux pane. We lock
 * after the first click and show a spinner on the chosen row until the
 * next state-poll clears the prompt.
 *
 * Affirm/deny styling is inferred ONLY from the digit position as a
 * gentle hint (first = primary, an option whose label starts with "No" =
 * deny-tinted) — never load-bearing: the digit is what's sent, the colour
 * is cosmetic, so a prompt-wording change can't mis-route a click.
 */
export function PermissionBlock({ sessionName, data, onSent }: Props) {
  const [clickedN, setClickedN] = useState<number | null>(null);
  // 409 = der Dialog steht nicht mehr (jemand anders hat geantwortet, Timeout).
  // Wird BENANNT gezeigt statt still zu scheitern — sonst klickt man dreimal
  // und wundert sich (Vertrag mit Cloud, 18.08.).
  const [stale, setStale] = useState(false);

  // Reset when a new prompt replaces the current one.
  useEffect(() => {
    setClickedN(null);
    setStale(false);
  }, [data]);

  const pick = async (n: number) => {
    if (clickedN !== null) return; // already in flight
    setClickedN(n);
    setStale(false);
    const opt = data.options.find((o) => o.n === n);
    // Server sagt, WIE geantwortet wird (ConPTY-Codex braucht teils eine
    // ganze Zeile); ohne Angabe bleibt es beim Bestand: Ziffer als Taste.
    const send = opt?.send ?? { type: 'key' as const, key: String(n) };
    try {
      await api.sessions.answerPrompt(sessionName, send, data.prompt_id);
      if (onSent) onSent();
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      if (status === 409) {
        setStale(true);          // veralteter Dialog — NICHT blind wiederholen
        setClickedN(null);
        return;
      }
      console.error('permission send failed', err);
      setClickedN(null); // unlock for retry
    }
  };

  const locked = clickedN !== null;

  return (
    <div className={`perm-block${locked ? ' is-sending' : ''}`}>
      <div className="perm-head">
        <ShieldAlert size={15} className="perm-head-icon" />
        <span className="perm-head-label">Agent wartet auf Freigabe</span>
      </div>
      {data.question && <div className="perm-question">{data.question}</div>}
      {data.context && (
        <div className="perm-context">
          {data.tool && <span className="perm-context-tool">{data.tool}</span>}
          <code className="perm-context-code">{data.context}</code>
        </div>
      )}
      <div className="perm-options">
        {data.options.map((opt) => {
          const isChosen = clickedN === opt.n;
          const isDeny = /^\s*no\b/i.test(opt.label);
          const isRisky = opt.risky === true;
          const isPrimary = opt.n === 1;
          const cls = (isChosen ? ' chosen' : isDeny ? ' deny' : isPrimary ? ' primary' : '')
            + (isRisky ? ' risky' : '');
          return (
            <button
              key={opt.n}
              className={`perm-option${cls}`}
              onClick={() => pick(opt.n)}
              disabled={locked}
              title={locked
                ? (isChosen ? 'Sendet …' : 'Andere Option wird gerade gesendet')
                : isRisky
                  ? `Weitreichend: ${opt.label} — bleibt über diesen Dialog hinaus gültig`
                  : `Sendet "${opt.n}" — diese Option wählen`}
            >
              <span className="perm-option-num">{opt.n}</span>
              <span className="perm-option-label">{opt.label}</span>
              {isChosen && (
                <span className="perm-option-spin" aria-label="Sendet">
                  <Loader2 size={12} />
                </span>
              )}
            </button>
          );
        })}
      </div>
      {stale && (
        <div className="perm-stale">
          Dieser Dialog ist nicht mehr aktuell — er wurde bereits beantwortet.
          Die Auswahl wurde NICHT gesendet.
        </div>
      )}
      <div className="perm-hint">
        {locked
          ? `Sende Option ${clickedN} … warte auf Bot`
          : 'Klick gibt frei bzw. lehnt ab · Esc im Pane bricht ab'}
      </div>
    </div>
  );
}
